import * as React from "react"
import { useSWRConfig } from "swr"

import {
  isAlfredBulkImportTerminal,
  useAlfredBulkImportJob,
} from "@/hooks/use-alfred-bulk-import-job"
import { toast } from "@/hooks/use-toast"
import {
  ALFRED_BULK_IMPORT_JOB_ID_STORAGE_KEY,
  ALFRED_BULK_IMPORT_NOTIFIED_JOB_ID_STORAGE_KEY,
  AlfredBulkImportJobContext,
} from "./alfred-bulk-import-job-context"
import type {
  AlfredBulkImportProgressJob,
  AlfredBulkImportUploadResult,
} from "@/types/alfred-bulk-import"
import type { AlfredBulkImportJobContextValue } from "./alfred-bulk-import-job-context"

function readStoredJobId(): string | null {
  if (typeof window === "undefined") return null
  return window.sessionStorage.getItem(ALFRED_BULK_IMPORT_JOB_ID_STORAGE_KEY)
}

function isAlfredDeviceCacheKey(key: unknown): boolean {
  if (!Array.isArray(key) || key[0] !== "alfred") return false

  return (
    key[1] === "devices" ||
    (key[1] === "clients" && typeof key[2] === "number" && key[3] === "devices")
  )
}

function notifyJobComplete(job: AlfredBulkImportProgressJob): void {
  const description = `${job.succeeded} succeeded, ${job.failed} failed, ${job.skippedDuplicate} skipped.`

  if (job.status === "COMPLETED") {
    toast({ title: "Bulk import completed", description, variant: "success" })
    return
  }

  if (job.status === "COMPLETED_WITH_ERRORS") {
    toast({ title: "Bulk import completed with errors", description, variant: "warning" })
    return
  }

  toast({ title: "Bulk import failed", description, variant: "error" })
}

export function AlfredBulkImportJobProvider({ children }: React.PropsWithChildren) {
  const [jobId, setJobId] = React.useState<string | null>(readStoredJobId)
  const [provisionalJob, setProvisionalJob] = React.useState<AlfredBulkImportProgressJob>()
  const { data, error, isLoading } = useAlfredBulkImportJob(jobId)
  const { mutate } = useSWRConfig()
  const job = data?.jobId === jobId ? data : provisionalJob?.jobId === jobId ? provisionalJob : undefined

  const startJob = React.useCallback((result: AlfredBulkImportUploadResult) => {
    window.sessionStorage.setItem(ALFRED_BULK_IMPORT_JOB_ID_STORAGE_KEY, result.jobId)
    window.sessionStorage.removeItem(ALFRED_BULK_IMPORT_NOTIFIED_JOB_ID_STORAGE_KEY)
    setProvisionalJob({
      jobId: result.jobId,
      status: result.status,
      totalRows: result.totalRows,
      processed: 0,
      succeeded: 0,
      skippedDuplicate: 0,
      failed: 0,
      errorSample: [],
    })
    setJobId(result.jobId)
  }, [])

  const dismissJob = React.useCallback(() => {
    window.sessionStorage.removeItem(ALFRED_BULK_IMPORT_JOB_ID_STORAGE_KEY)
    window.sessionStorage.removeItem(ALFRED_BULK_IMPORT_NOTIFIED_JOB_ID_STORAGE_KEY)
    setProvisionalJob(undefined)
    setJobId(null)
  }, [])

  React.useEffect(() => {
    if (!job || !isAlfredBulkImportTerminal(job.status)) return
    if (
      window.sessionStorage.getItem(ALFRED_BULK_IMPORT_NOTIFIED_JOB_ID_STORAGE_KEY) ===
      job.jobId
    ) {
      return
    }

    window.sessionStorage.setItem(ALFRED_BULK_IMPORT_NOTIFIED_JOB_ID_STORAGE_KEY, job.jobId)
    notifyJobComplete(job)
    void mutate(isAlfredDeviceCacheKey)
  }, [job, mutate])

  const value = React.useMemo<AlfredBulkImportJobContextValue>(
    () => ({ jobId, job, error, isLoading, startJob, dismissJob }),
    [dismissJob, error, isLoading, job, jobId, startJob],
  )

  return (
    <AlfredBulkImportJobContext.Provider value={value}>
      {children}
    </AlfredBulkImportJobContext.Provider>
  )
}
